// ** React Imports
import { Fragment, useState, MouseEvent, useEffect } from 'react';

// ** MUI Imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import TimelineDot from '@mui/lab/TimelineDot';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TimelineItem from '@mui/lab/TimelineItem';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import TimelineContent from '@mui/lab/TimelineContent';
import TableContainer from '@mui/material/TableContainer';
import Link from '@mui/material/Link';
import { v4 as uuid } from 'uuid';
import CustomChip from 'src/materio/@core/components/mui/chip';
// ** Types
import { ThemeColor } from 'src/materio/@core/layouts/types';
import { Button, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutline from 'mdi-material-ui/DeleteOutline';
import EyeOutline from 'mdi-material-ui/EyeOutline';
import DotsVertical from 'mdi-material-ui/DotsVertical';
import PencilOutline from 'mdi-material-ui/PencilOutline';
import Download from 'mdi-material-ui/Download';
import ContentCopy from 'mdi-material-ui/ContentCopy';
import { client } from '../../../utils/axios-client';
import PageHeader from '../../../materio/@core/components/page-header';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../hooks/useAwaitableComponent';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { ApiResponseType } from '../../../types';
import ApplicationStore from '../../../stores/ApplicationStore';
import {
    ActivityTemplateSelectionStore,
    Lead,
    LeadActivityEmailStore,
    LeadActivityOutcomeStore,
    LeadActivityTemplateStore,
    LeadScreenStore,
    PlaybookTemplate,
} from '../../../stores/LeadStore';
import { leadEndpoints } from '../../../configs/endpoints';
import StandardSkeleton from '../../common/StandardSkeleton';
import GridSkeleton from '../../common/GridSkeleton';

const ActivityTemplateSelection = () => {
    /*const playbook = PlaybookStore.useState(s => s.selectedPlaybook);*/
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead) as Lead;
    const leadActivityTemplates = LeadActivityTemplateStore.useState((s) => s.record);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        const fetchCadence = async () => {
            setHasLoaded(false);
            const response = await client.get(
                leadEndpoints.gettemplateselections.replace('{leadid}', '' + selectedLead.id.toString()),
            );
            LeadActivityTemplateStore.update((s) => {
                s.record = response.data;
            });
            setHasLoaded(true);
        };
        if (selectedLead != null) fetchCadence();
    }, []);

    const onSelectItem = async (row: PlaybookTemplate) => {
        const response = await client.get('/api/leads/gettemplate', {
            params: { id: row.id, leadId: selectedLead.id },
        });

        switch (response.data.templateType) {
            case 'Email':
                LeadActivityOutcomeStore.update((s) => {
                    s.record.emailSubject = response.data.subject;
                });
                LeadActivityEmailStore.update((s) => {
                    s.record.emailBody = response.data.body;
                });
                break;
            case 'Social Media':
                LeadActivityOutcomeStore.update((s) => {
                    s.record.socialMediaBody = response.data.body;
                });
                break;
            case 'SMS':
                LeadActivityOutcomeStore.update((s) => {
                    s.record.smsBody = response.data.body;
                });
                break;
            default:
            // code block
        }

        ActivityTemplateSelectionStore.update((s) => {
            s.activityTemplateSelectionMode = false;
        });
    };

    if (hasLoaded) {
        return (
            <Fragment>
                <Card sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 16, color: 'CornflowerBlue' }}>Templates</Typography>
                    <Divider sx={{ m: 0 }} />
                    {leadActivityTemplates?.playbookTemplates.length > 0 && (
                        <TableContainer>
                            <Table size="small" sx={{ minWidth: 300 }}>
                                <TableHead
                                    sx={{
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
                                    }}
                                ></TableHead>

                                <TableBody>
                                    {leadActivityTemplates?.playbookTemplates.map((item, index: number) => (
                                        <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                            <TableCell sx={{ width: 150, textAlign: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Tooltip title="Select">
                                                        <Box>
                                                            <Link
                                                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                                                                underline="hover"
                                                                component="button"
                                                                variant="caption"
                                                                onClick={() => onSelectItem(item)}
                                                            >
                                                                {item.name}
                                                            </Link>
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {leadActivityTemplates?.playbookTemplates.length == 0 && <Typography>None Available</Typography>}
                </Card>
                <Card sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 16, color: 'CornflowerBlue' }}>Template Variants</Typography>
                    <Divider sx={{ m: 0 }} />
                    {leadActivityTemplates?.playbookTemplateVariants.length > 0 && (
                        <TableContainer>
                            <Table size="small" sx={{ minWidth: 300 }}>
                                <TableHead
                                    sx={{
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'light' ? 'grey.50' : 'background.default',
                                    }}
                                ></TableHead>

                                <TableBody>
                                    {leadActivityTemplates?.playbookTemplateVariants.map((item, index: number) => (
                                        <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                            <TableCell sx={{ width: 150, textAlign: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Tooltip title="Select">
                                                        <Box>
                                                            <Link
                                                                sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                                                                underline="hover"
                                                                component="button"
                                                                variant="caption"
                                                                onClick={() => onSelectItem(item)}
                                                            >
                                                                {item.name}
                                                            </Link>
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {leadActivityTemplates?.playbookTemplateVariants.length == 0 && (
                        <Typography>None Available</Typography>
                    )}
                </Card>
            </Fragment>
        );
    } else {
        return <GridSkeleton />;
    }
};

export default ActivityTemplateSelection;
